import * as api from '@apis/admin-review-events';
import { CreateReviewEventDto, UpdateReviewEventDto } from '@models/review-event';
import { useMutation } from '@tanstack/react-query';
import { Button, DatePicker, Form, Input, Modal, Switch } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

interface ReviewEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: UpdateReviewEventDto;
  mode: 'create' | 'edit';
}

export const ReviewEventModal: React.FC<ReviewEventModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialData,
  mode,
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && initialData) {
      form.setFieldsValue({
        ...initialData,
        startDate: initialData.startDate ? dayjs(initialData.startDate) : undefined,
        endDate: initialData.endDate ? dayjs(initialData.endDate) : undefined,
        announcementDate: initialData.announcementDate ? dayjs(initialData.announcementDate) : undefined,
      });
    } else {
      form.resetFields();
    }
  }, [isOpen, initialData, form]);

  const createMutation = useMutation({
    mutationFn: api.createReviewEvent,
    onSuccess: () => {
      toast.dark('리뷰 이벤트가 생성되었습니다', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        bodyStyle: {
          color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
          textAlign: 'center',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px',
        },
        theme: 'dark',
      });
      onSuccess();
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: api.updateReviewEvent,
    onSuccess: () => {
      toast.dark('리뷰 이벤트가 수정되었습니다', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        bodyStyle: {
          color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
          textAlign: 'center',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px',
        },
        theme: 'dark',
      });
      onSuccess();
      onClose();
    },
  });

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      const submitData = {
        ...values,
        startDate: moment(values.startDate?.toDate())
          .startOf('day')
          .toDate(),
        endDate: moment(values.endDate?.toDate())
          .endOf('day')
          .toDate(),
        announcementDate: moment(values.announcementDate?.toDate())
          .startOf('day')
          .toDate(),
      };

      if (mode === 'create') {
        await createMutation.mutateAsync(submitData as CreateReviewEventDto);
      } else {
        await updateMutation.mutateAsync({
          _id: initialData?._id,
          ...submitData,
        } as UpdateReviewEventDto);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={mode === 'create' ? '리뷰 이벤트 생성' : '리뷰 이벤트 수정'}
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
        <Button key="submit" type="primary" loading={isSubmitting} onClick={handleSubmit}>
          {mode === 'create' ? '생성' : '수정'}
        </Button>,
      ]}
      width={600}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          photoReviewOnly: false,
        }}>
        <Form.Item name="title" label="제목" rules={[{ required: true, message: '제목을 입력해주세요' }]}>
          <Input placeholder="제목을 입력해주세요" />
        </Form.Item>

        <Form.Item name="description" label="설명" rules={[{ required: true, message: '설명을 입력해주세요' }]}>
          <Input.TextArea rows={4} placeholder="설명을 입력해주세요" />
        </Form.Item>

        <Form.Item name="startDate" label="시작일" rules={[{ required: true, message: '시작일을 선택해주세요' }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="endDate" label="종료일" rules={[{ required: true, message: '종료일을 선택해주세요' }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="announcementDate"
          label="발표일"
          rules={[{ required: true, message: '발표일을 선택해주세요' }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="photoReviewOnly" label="사진 리뷰만" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};
